import { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { recordingRequested } from '../redux/webcamRecorderSlice'
import { isRecording as isReturnRecording } from '../redux/returnVideoRecorderSlice'
import { hasPendingWrites } from '../helpers/axiosInstance'
import { hasUnloadGuards } from '../helpers/unloadGuards'

// "Would a reload right now destroy something?"
//
// A keystroke listener alone cannot answer that. It sees native `input` events in this document and
// nothing else, which leaves out most of the places an operator actually has work invested: a
// packing-proof video mid-recording, a write already issued from a scanner callback, a flow that has
// asked the browser to confirm before unloading. Every signal that can answer it lives here, so
// there is one thing to consult and one thing to extend.
//
// It over-reports on purpose. The cost of a false "yes" is that this tab waits for the next
// opportunity; the cost of a false "no" is an operator's packing session.

// Typing older than this is not unsaved work worth keeping a whole tab on a stale bundle for.
// Without a decay the latch is permanent — one scan into a search box at the start of the shift and
// a tab that never changes route is never eligible for the background reload again, which is
// precisely the tab that trigger exists for.
export const TYPING_DECAY_MS = 5 * 60 * 1000

export const useUnsavedWork = () => {
  const lastInputAtRef = useRef(null)

  // An in-progress packing-proof or return video. The recorder buffers chunks in memory and its
  // state lives only in redux, so a reload does not interrupt the recording — it destroys it, along
  // with the multipart upload it was feeding. The packing recorder lives in Layout1 and keeps
  // recording across the packing routes (tote scan and order); the return one lives in the receive
  // screen. A navigation can land while either is recording, so this has to guard the navigation
  // trigger too, not just the hidden-tab one.
  const packingRecording = useSelector(recordingRequested)
  const returnRecording = useSelector(isReturnRecording)
  const recordingRef = useRef(false)
  recordingRef.current = Boolean(packingRecording) || Boolean(returnRecording)

  useEffect(() => {
    const remember = () => {
      lastInputAtRef.current = Date.now()
    }
    // Capture phase: nothing in the tree can stop this from being seen. Barcode scanners are
    // keyboards as far as the DOM is concerned, so a scan into any field lands here too.
    document.addEventListener('input', remember, true)
    return () => document.removeEventListener('input', remember, true)
  }, [])

  const hasUnsavedWork = useCallback(() => {
    if (lastInputAtRef.current !== null && Date.now() - lastInputAtRef.current < TYPING_DECAY_MS) return true
    if (recordingRef.current) return true
    if (hasPendingWrites()) return true
    if (hasUnloadGuards()) return true
    return false
  }, [])

  // A new screen is a clean slate for typing specifically. Everything else on the list is app-wide
  // and outlives the route, which is the whole reason it is not inferred from keystrokes.
  const forgetTyping = useCallback(() => {
    lastInputAtRef.current = null
  }, [])

  return { hasUnsavedWork, forgetTyping }
}
